/**
 * Timeline — dated events on a single rail, newest first.
 *
 * Built for a property's history, where three kinds of thing share one story:
 * visits PazNetworks delivered, trips currently scheduled, and coverage the
 * customer has planned. Kind is carried by a labelled marker, never by colour
 * alone, and each entry names its kind in text.
 */
import React from 'react';
import { cn } from '../lib/utils';

export type TimelineTone = 'past' | 'current' | 'future' | 'planned';

export interface TimelineEntry {
  id: string;
  /** Date or range, already formatted for reading. */
  when: string;
  title: string;
  /** Short kind label: "Delivered", "Scheduled", "Planned". */
  kindLabel: string;
  tone: TimelineTone;
  detail?: string | null;
  onSelect?: () => void;
}

export interface TimelineProps {
  entries: TimelineEntry[];
  emptyLabel: string;
  className?: string;
}

const MARKER: Record<TimelineTone, string> = {
  past: 'bg-text-faint',
  current: 'bg-success',
  future: 'bg-primary',
  planned: 'bg-primary/40 ring-1 ring-primary',
};

const KIND_TEXT: Record<TimelineTone, string> = {
  past: 'text-text-muted',
  current: 'text-success',
  future: 'text-primary',
  planned: 'text-primary',
};

export function Timeline({ entries, emptyLabel, className }: TimelineProps) {
  if (entries.length === 0) {
    return <p className={cn('text-sm text-text-faint', className)}>{emptyLabel}</p>;
  }

  return (
    <ol className={cn('relative space-y-0', className)}>
      {entries.map((entry, index) => {
        const last = index === entries.length - 1;
        const content = (
          <>
            <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
                {entry.when}
              </span>
              <span className={cn('text-[11px] font-medium', KIND_TEXT[entry.tone])}>
                {entry.kindLabel}
              </span>
            </span>
            <span className="mt-0.5 block text-sm leading-snug text-text">{entry.title}</span>
            {entry.detail && (
              <span className="mt-0.5 block text-xs text-text-muted">{entry.detail}</span>
            )}
          </>
        );

        return (
          <li key={entry.id} className="relative pb-4 pl-6 last:pb-0">
            {/* The rail stops at the last marker instead of trailing into space. */}
            {!last && (
              <span
                aria-hidden="true"
                className="absolute left-[5px] top-3 h-full w-px bg-divider"
              />
            )}
            <span
              aria-hidden="true"
              className={cn('absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full', MARKER[entry.tone])}
            />
            {entry.onSelect ? (
              <button
                type="button"
                onClick={entry.onSelect}
                className="block w-full rounded-sm text-left hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {content}
              </button>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ol>
  );
}
